import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [state, setState] = useState("checking"); // checking | ready | invalid
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api
      .get(`/auth/activate/${token}`)
      .then(({ data }) => { setInfo(data); setState("ready"); })
      .catch((err) => { setError(apiError(err.response?.data?.detail)); setState("invalid"); });
  }, [token]);

  const activate = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate", {
        token,
        password: password || null,
      });
      setUser(data.user);
      toast.success("Account activated");
      navigate("/community");
    } catch (err) {
      setError(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#0D0F12] px-6">
      <div className="w-full max-w-md rounded-2xl bg-card p-8 shadow-xl">
        <Logo wm={28} tone="light" />
        {state === "checking" && <p className="mt-6 text-sm text-muted-foreground">Checking your invitation…</p>}

        {state === "invalid" && (
          <div className="mt-6" data-testid="activate-invalid">
            <h2 className="font-heading text-[22px] font-semibold">Invitation unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {error || "This invitation link is invalid or has expired."}
            </p>
            <Button className="mt-5 h-11 w-full" variant="outline" onClick={() => navigate("/login")}>
              Go to sign in
            </Button>
          </div>
        )}

        {state === "ready" && info && (
          <div className="mt-6" data-testid="activate-ready">
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-[#33B6E4]/10 px-2.5 py-0.5 text-xs font-medium text-[#1779AC]">
              <CheckCircle2 className="h-3.5 w-3.5" /> {ROLE_LABELS[info.role]}
            </div>
            <h2 className="mt-2 font-heading text-[24px] font-semibold">Welcome, {info.name?.split(" ")[0]}.</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              You're activating <strong>{info.email}</strong>.{" "}
              {info.needs_password
                ? "As an admin account, please set a password to continue."
                : "One click and you're in. No password required."}
            </p>

            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-medium">
                {info.needs_password ? "Create a password" : "Set a password (optional)"}
              </label>
              <input
                data-testid="activate-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 w-full rounded-lg border border-input bg-card px-3.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder="At least 8 characters"
              />
              {!info.needs_password && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  With a password you can sign in directly next time instead of waiting for an email link. You can also set one later from your profile.
                </p>
              )}
            </div>

            {error && <div className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}

            <Button
              onClick={activate}
              disabled={loading}
              data-testid="activate-submit-button"
              className="btn-brand mt-5 h-11 w-full"
            >
              {loading ? "Activating…" : info.needs_password ? "Set password & enter" : password ? "Set password & enter" : "Enter the academy"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
